import { useState } from "react";
import { addDocument } from "../../../services/firestoreServices";
import { uploadFiles, File as StorageFile } from "../../../services/storage";
import * as DocumentPicker from "expo-document-picker";

interface UseFileUploadProps {
  folderId?: string;
  companyId: string;
}

export function useFileUpload({ folderId, companyId }: UseFileUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const pickAndUploadFiles = async () => {
    try {
      setError("");
      setUploading(true);

      // Pick files
      const result = await DocumentPicker.getDocumentAsync({
        type: "*/*",
        multiple: true,
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        setUploading(false);
        return;
      }

      // Prepare files for upload
      const filesToUpload: StorageFile[] = result.assets.map((asset) => ({
        uri: asset.uri,
        name: asset.name,
        mimeType: asset.mimeType || "application/octet-stream",
      }));

      // Upload files to storage
      const uploadedFiles = await uploadFiles(filesToUpload, companyId);

      // Save file metadata to Firestore
      await Promise.all(
        uploadedFiles.map(async (file) => {
          const now = new Date();
          const fileSize = result.assets.find(
            (a) => a.name === file.name,
          )?.size;

          await addDocument(
            { name: "drive_files" },
            {
              name: file.name,
              createdAt: now,
              updatedAt: now,
              size: formatFileSize(fileSize || 0),
              ...(folderId && { folderId }),
              companyId,
              deleted: false,
              url: file.uri,
              type: file.mimeType,
            },
          );
        }),
      );

      setUploading(false);
    } catch (error) {
      console.error("Failed to upload files:", error);
      setError("Failed to upload files. Please try again.");
      setUploading(false);
    }
  };

  return {
    uploading,
    error,
    pickAndUploadFiles,
  };
}

// Helper function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}
